import { differenceInCalendarDays, format } from "date-fns";

export type BirthdayEntry = {
    uid: string;
    displayName: string;
    /** Birthday date within the viewed year/month */
    occurrence: Date;
    /** Calendar days from referenceDate to occurrence */
    daysAway: number;
};

export type BirthdayGroupKey = "today" | "thisWeek" | "laterThisMonth" | "earlierThisMonth" | "inMonth";

export type BirthdayGroup = {
    key: BirthdayGroupKey;
    label: string;
    entries: BirthdayEntry[];
};

type BirthdayMember = {
    uid: string;
    displayName?: string;
    birthday?: { month: number; day: number };
};

type GroupBirthdaysParams = {
    members: BirthdayMember[];
    /** "today" */
    referenceDate: Date;
    viewYear: number;
    /** 0-indexed */
    viewMonth: number;
};

const GROUP_ORDER: BirthdayGroupKey[] = ["today", "thisWeek", "laterThisMonth", "earlierThisMonth", "inMonth"];

function getGroupLabel(key: BirthdayGroupKey, monthName: string): string {
    switch (key) {
        case "today":
            return "Today";
        case "thisWeek":
            return "This week";
        case "laterThisMonth":
            return "Later this month";
        case "earlierThisMonth":
            return "Earlier this month";
        case "inMonth":
            return `In ${monthName}`;
        default: {
            const exhaustiveCheck: never = key;
            return exhaustiveCheck;
        }
    }
}

function getBucketKey(daysAway: number, isCurrentMonth: boolean): BirthdayGroupKey {
    if (!isCurrentMonth) {
        return "inMonth";
    }
    if (daysAway === 0) {
        return "today";
    }
    if (daysAway < 0) {
        return "earlierThisMonth";
    }
    if (daysAway <= 7) {
        return "thisWeek";
    }
    return "laterThisMonth";
}

/**
 * Builds a birthday occurrence within the viewed year/month.
 * Clamps invalid days (e.g. 29 Feb in a non-leap year) to the last day of the month
 * so JS does not roll the date into the following month.
 */
function buildOccurrence(viewYear: number, viewMonth: number, dobDay: number): Date {
    const occurrence = new Date(viewYear, viewMonth, dobDay);
    if (occurrence.getMonth() !== viewMonth) {
        return new Date(viewYear, viewMonth + 1, 0);
    }
    return occurrence;
}

function toBirthdayEntry(
    member: BirthdayMember,
    viewYear: number,
    viewMonth: number,
    referenceDate: Date
): BirthdayEntry | null {
    if (!member.birthday || member.birthday.month - 1 !== viewMonth) {
        return null;
    }
    const occurrence = buildOccurrence(viewYear, viewMonth, member.birthday.day);
    return {
        daysAway: differenceInCalendarDays(occurrence, referenceDate),
        displayName: member.displayName ?? "Unknown",
        occurrence,
        uid: member.uid,
    };
}

function bucketEntries(entries: BirthdayEntry[], isCurrentMonth: boolean, monthName: string): BirthdayGroup[] {
    const buckets = new Map<BirthdayGroupKey, BirthdayEntry[]>();
    for (const entry of entries) {
        const key = getBucketKey(entry.daysAway, isCurrentMonth);
        const existing = buckets.get(key);
        if (existing) {
            existing.push(entry);
        } else {
            buckets.set(key, [entry]);
        }
    }

    const groups: BirthdayGroup[] = [];
    for (const key of GROUP_ORDER) {
        const bucket = buckets.get(key);
        if (!bucket || bucket.length === 0) {
            continue;
        }
        bucket.sort((a, b) => a.occurrence.getTime() - b.occurrence.getTime());
        groups.push({ entries: bucket, key, label: getGroupLabel(key, monthName) });
    }
    return groups;
}

export function groupBirthdays({ members, referenceDate, viewYear, viewMonth }: GroupBirthdaysParams): BirthdayGroup[] {
    const isCurrentMonth = viewYear === referenceDate.getFullYear() && viewMonth === referenceDate.getMonth();
    const monthName = format(new Date(viewYear, viewMonth, 1), "MMMM");

    const entries: BirthdayEntry[] = [];
    for (const member of members) {
        const entry = toBirthdayEntry(member, viewYear, viewMonth, referenceDate);
        if (entry) {
            entries.push(entry);
        }
    }

    return bucketEntries(entries, isCurrentMonth, monthName);
}
